import React, { useEffect, useCallback } from 'react';
import PermissionGate from './PermissionGate';
import { getImageTypeConfig } from '../utils/propertyUtils';

/**
 * PropertyPhotoLightboxModal
 *
 * Props:
 *   show            {bool}
 *   images          {Array}   full list of images being browsed
 *   currentIndex    {number}  index into `images`
 *   onClose         {function}
 *   onNavigate      {function(newIndex)}
 *   onDelete        {function(image)}
 *   onSetDefault    {function(image)}
 */
const PropertyPhotoLightboxModal = ({
  show,
  images,
  currentIndex,
  onClose,
  onNavigate,
  onDelete,
  onSetDefault,
}) => {
  const image = images?.[currentIndex] ?? null;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < (images?.length ?? 0) - 1;

  const nav = useCallback(
    (dir) => {
      const next = currentIndex + dir;
      if (next >= 0 && next < images.length) onNavigate(next);
    },
    [currentIndex, images, onNavigate]
  );

  // Keyboard support
  useEffect(() => {
    if (!show) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') nav(-1);
      else if (e.key === 'ArrowRight') nav(1);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, nav, onClose]);

  if (!show || !image) return null;

  const typeConfig = getImageTypeConfig(image.image_type);
  const fileUrl = image.file?.file_url || image.file?.medium_url || '';
  const filename = image.file?.file_name || 'Photo';
  const isProfile = image.image_type === 'profile';

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1055 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="modal fade show d-block"
        style={{ zIndex: 1056 }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '92vw' }}>
          <div className="modal-content bg-dark text-white border-0 rounded-3 overflow-hidden">

            {/* Header */}
            <div className="modal-header border-secondary px-4 py-3">
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${typeConfig.badgeClass}`}>{typeConfig.label}</span>
                <span className="text-muted small">{filename}</span>
                {image.caption && (
                  <span className="text-white-50 small fst-italic">— {image.caption}</span>
                )}
              </div>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <span className="text-muted small">
                  {currentIndex + 1} / {images.length}
                </span>
                <button
                  type="button"
                  className="btn-close btn-close-white ms-2"
                  onClick={onClose}
                  aria-label="Close"
                />
              </div>
            </div>

            {/* Body — image + nav arrows */}
            <div
              className="modal-body position-relative p-0 d-flex align-items-center justify-content-center"
              style={{ minHeight: '60vh', backgroundColor: '#111' }}
            >
              {/* Prev arrow */}
              <button
                type="button"
                className="btn btn-dark position-absolute start-0 top-50 translate-middle-y ms-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 44, height: 44, opacity: hasPrev ? 0.85 : 0.2 }}
                disabled={!hasPrev}
                onClick={() => nav(-1)}
                title="Previous (←)"
              >
                <i className="bi bi-chevron-left fs-5"></i>
              </button>

              <img
                src={fileUrl}
                alt={filename}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  display: 'block',
                  userSelect: 'none',
                }}
                draggable={false}
              />

              {/* Next arrow */}
              <button
                type="button"
                className="btn btn-dark position-absolute end-0 top-50 translate-middle-y me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 44, height: 44, opacity: hasNext ? 0.85 : 0.2 }}
                disabled={!hasNext}
                onClick={() => nav(1)}
                title="Next (→)"
              >
                <i className="bi bi-chevron-right fs-5"></i>
              </button>
            </div>

            {/* Footer */}
            <div className="modal-footer border-secondary px-4 py-3 d-flex flex-wrap gap-2">
              {/* Set as default — only enabled for profile-type images */}
              <PermissionGate requirePermission="properties.manage">
                <span
                  title={
                    isProfile
                      ? image.is_default
                        ? 'This is already the cover photo'
                        : 'Set as cover photo'
                      : 'Only profile photos can be set as cover. Change image type to Profile first.'
                  }
                >
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      image.is_default ? 'btn-warning' : 'btn-outline-warning'
                    }`}
                    disabled={!isProfile || image.is_default}
                    style={!isProfile ? { pointerEvents: 'none', opacity: 0.45 } : {}}
                    onClick={() => onSetDefault(image)}
                  >
                    <i className={`bi ${image.is_default ? 'bi-star-fill' : 'bi-star'} me-1`}></i>
                    {image.is_default ? 'Cover photo' : 'Set as cover'}
                  </button>
                </span>
              </PermissionGate>

              <div className="ms-auto d-flex gap-2">
                <PermissionGate requirePermission="properties.manage">
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => onDelete(image)}
                  >
                    <i className="bi bi-trash me-1"></i>Remove
                  </button>
                </PermissionGate>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyPhotoLightboxModal;

